import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import {
  PageHeader, PrimaryButton, GhostButton, Modal, Field, TextInput, StatusPill, useFetch,
} from "../../components/crm/ui";
import { ChartCard, MiniPie, MiniBar, KpiChip, SectionCard, SectionHeader, CHART_COLORS } from "./dashboard/dashboardUi";
import {
  Plus, Target, Trash2, Eye, ChevronLeft, ChevronRight, Check, MapPin, Users, Gauge, Search, Gift,
} from "lucide-react";

const METRIC_LABEL = { kw: "kW Installed", units: "Units Installed" };
const STEP_TITLES = ["Name", "Region", "SC Coverage", "Target", "Incentive", "Confirm"];
const GAUGE_BANDS = [{ to: 40, color: "#DC2626" }, { to: 75, color: "#F59E0B" }, { to: 100, color: "#1F4E2C" }];

// Builds a YYYY-MM-DD string from a LOCAL date's own y/m/d parts — never
// Date#toISOString(), which converts to UTC first and silently shifts the
// date back by one day for every timezone ahead of UTC (e.g. all of India).
function toISODate(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function monthRange(offset = 0) {
  const d = new Date(); const m = d.getMonth() + offset; const y = d.getFullYear() + Math.floor(m / 12);
  const mm = ((m % 12) + 12) % 12;
  return [toISODate(new Date(y, mm, 1)), toISODate(new Date(y, mm + 1, 0))];
}
function quarterRange(offset = 0) {
  const d = new Date(); const q = Math.floor(d.getMonth() / 3) + offset; const y = d.getFullYear() + Math.floor(q / 4);
  const qq = ((q % 4) + 4) % 4;
  return [toISODate(new Date(y, qq * 3, 1)), toISODate(new Date(y, qq * 3 + 3, 0))];
}

// Fresh object every call (not a module-load constant) so dates never go
// stale if the CRM tab is left open across a month/quarter boundary.
function emptyForm() {
  const [start, end] = monthRange(0);
  return {
    name: "", states: [], sc_ids: [], metric: "kw", target_value: "", period_type: "month",
    start_date: start, end_date: end, incentive_amount: "", notes: "", preset: "this_month",
  };
}

/** CRM-themed half-donut gauge — red/amber/green bands + a needle, matching the
 * one already shipped in the SC portal, restyled for the admin console palette. */
function AdminGauge({ pct, size = 150, stroke = 15 }) {
  const p = Math.max(0, Math.min(100, pct || 0));
  const r = (size - stroke) / 2; const cx = size / 2; const cy = size / 2;
  const toXY = (deg) => { const rad = (deg * Math.PI) / 180; return { x: cx + r * Math.cos(rad), y: cy - r * Math.sin(rad) }; };
  let prevTo = 0;
  const segments = GAUGE_BANDS.map((z) => {
    const a = toXY(180 - (prevTo / 100) * 180); const b = toXY(180 - (z.to / 100) * 180);
    const seg = { d: `M ${a.x} ${a.y} A ${r} ${r} 0 0 1 ${b.x} ${b.y}`, color: z.color }; prevTo = z.to; return seg;
  });
  const needleRad = ((180 - (p / 100) * 180) * Math.PI) / 180;
  const nx = cx + r * 0.8 * Math.cos(needleRad); const ny = cy - r * 0.8 * Math.sin(needleRad);
  return (
    <div className="flex flex-col items-center" data-testid="admin-sales-target-gauge">
      <svg width={size} height={size / 2 + stroke / 2 + 4} viewBox={`0 0 ${size} ${size / 2 + stroke / 2 + 4}`}>
        {segments.map((s, i) => <path key={i} d={s.d} fill="none" stroke={s.color} strokeWidth={stroke} opacity={0.9} />)}
        <circle cx={cx} cy={cy} r={6} fill="#0A1128" />
        <line x1={cx} y1={cy} x2={nx} y2={ny} stroke="#0A1128" strokeWidth={3.5} strokeLinecap="round" />
      </svg>
      <div className="font-display text-3xl font-bold text-[#0A1128] -mt-1">{p}%</div>
    </div>
  );
}

export default function SalesTargets() {
  const { data: targets, loading, reload } = useFetch("/sales-targets");
  const [overview, setOverview] = useState(null);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [detailId, setDetailId] = useState(null);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    api.get("/sales-targets/overview").then((r) => setOverview(r.data)).catch((err) => toast.error(formatApiError(err.response?.data?.detail) || err.message));
  }, [targets]);
  useEffect(() => {
    if (!detailId) { setDetail(null); return; }
    api.get(`/sales-targets/${detailId}`).then((r) => setDetail(r.data)).catch((err) => { toast.error(formatApiError(err.response?.data?.detail) || err.message); setDetailId(null); });
  }, [detailId]);

  async function remove(t) {
    if (!window.confirm(`Delete target "${t.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/sales-targets/${t.id}`);
      toast.success("Target deleted"); reload();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  }

  const statusTone = { active: "success", upcoming: "info", completed: "muted" };

  return (
    <div data-testid="sales-targets-page">
      <PageHeader
        overline="Performance · SC-wise Installation Targets"
        title="Sales Target"
        action={<PrimaryButton onClick={() => setWizardOpen(true)} testid="new-sales-target-btn"><Plus className="w-4 h-4" /> New Target</PrimaryButton>}
      />

      {/* Overview */}
      <SectionCard accent="sc" testid="sales-target-overview">
        <SectionHeader icon={Gauge} title="Target vs Achievement" subtitle={`${overview?.active_target_count ?? 0} active target(s) · ${overview?.sc_coverage_count ?? 0} SCs covered`} accent="sc" />
        {!overview || (!overview.kw && !overview.units) ? (
          <div className="text-sm font-mono text-[#94A3B8] py-6 text-center" data-testid="sales-target-overview-empty">No active targets right now. Create one to see live Target vs Achievement here.</div>
        ) : (
          <div className={"grid grid-cols-1 gap-4 " + (overview.kw && overview.units ? "md:grid-cols-2" : "md:grid-cols-1")}>
            {["kw", "units"].map((metric) => {
              const g = overview[metric];
              if (!g) return null;
              return (
                <div key={metric} className={"border border-[#E2E4DC] p-4 " + (overview.kw && overview.units ? "" : "md:max-w-xl")} data-testid={`sales-target-overview-${metric}`}>
                  <div className="flex items-center gap-4">
                    <AdminGauge pct={g.progress_pct} />
                    <div className="flex-1">
                      <div className="font-mono text-[10px] uppercase tracking-widest text-[#4A5568]">{METRIC_LABEL[metric]}</div>
                      <div className="font-display text-xl font-bold text-[#0A1128] mt-1">
                        {g.total_achieved.toLocaleString("en-IN")} / {g.total_target.toLocaleString("en-IN")}
                      </div>
                      <div className="flex gap-1.5 mt-2 flex-wrap">
                        <KpiChip label="Targets" value={g.targets.length} tone="bg-slate-100 text-slate-800" />
                      </div>
                    </div>
                  </div>
                  {g.targets.length > 0 && (
                    <div className="mt-3">
                      <ChartCard title="Target-wise Progress %" testid={`sales-target-rank-${metric}`}>
                        <MiniBar data={g.targets.map((t) => ({ name: t.name.slice(0, 14), value: t.progress_pct }))} xKey="name" dataKey="value" color="#1F4E2C" formatter={(v) => `${v}%`} />
                      </ChartCard>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </SectionCard>

      {/* List */}
      <div className="bg-white border border-[#DDE5D8] mt-5">
        <table className="w-full text-sm" data-testid="sales-targets-table">
          <thead className="bg-[#F4F6F1] border-b border-[#DDE5D8]">
            <tr>
              {["Name", "Period", "Region", "SCs", "Metric", "Achieved vs Target", "Incentive/SC", "Status", "Actions"].map((h) => (
                <th key={h} className="px-4 py-3 text-left font-mono text-[10px] uppercase tracking-[0.2em] text-[#475569]">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(targets || []).map((t) => (
              <tr key={t.id} data-testid={`sales-target-row-${t.id}`} className="border-b border-[#EEF2EA] hover:bg-[#F4F6F1]">
                <td className="px-4 py-3 font-display font-semibold">{t.name}</td>
                <td className="px-4 py-3 font-mono text-[11px] text-[#4A5568]">{t.start_date} → {t.end_date}</td>
                <td className="px-4 py-3 font-mono text-[11px] text-[#4A5568]">{t.region_count} state(s)</td>
                <td className="px-4 py-3 font-mono text-[11px] text-[#4A5568]">{t.sc_count}</td>
                <td className="px-4 py-3 font-mono text-[11px] text-[#4A5568]">{METRIC_LABEL[t.metric]}</td>
                <td className="px-4 py-3">
                  <div className="font-mono text-xs font-semibold text-[#1F4E2C]">{t.achieved_value.toLocaleString("en-IN")} / {t.target_value.toLocaleString("en-IN")} ({t.progress_pct}%)</div>
                  <div className="h-1.5 bg-[#EEF2EA] mt-1 w-32"><div className="h-full bg-[#1F4E2C]" style={{ width: `${Math.min(100, t.progress_pct)}%` }} /></div>
                </td>
                <td className="px-4 py-3 font-mono text-[11px] font-semibold text-[#6D28D9]" data-testid={`sales-target-incentive-${t.id}`}>₹{(t.incentive_amount || 0).toLocaleString("en-IN")}</td>
                <td className="px-4 py-3"><StatusPill value={t.status} tone={statusTone[t.status] || "default"} /></td>
                <td className="px-4 py-3">
                  <div className="inline-flex items-center gap-1">
                    <button onClick={() => setDetailId(t.id)} data-testid={`view-sales-target-${t.id}`} className="p-1 text-[#475569] hover:text-[#1F4E2C]"><Eye className="w-3.5 h-3.5" /></button>
                    <button onClick={() => remove(t)} data-testid={`del-sales-target-${t.id}`} className="p-1 text-[#475569] hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                </td>
              </tr>
            ))}
            {!loading && (!targets || targets.length === 0) && (
              <tr><td colSpan={9} className="px-4 py-12 text-center text-sm font-mono text-[#94A3B8]" data-testid="sales-targets-empty">No targets created yet. Click "New Target" to set your first SC-wise installation target.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <TargetWizard open={wizardOpen} onClose={() => setWizardOpen(false)} onCreated={() => { setWizardOpen(false); reload(); }} />
      <TargetDetail target={detail} onClose={() => setDetailId(null)} />
    </div>
  );
}

function TargetDetail({ target, onClose }) {
  return (
    <Modal open={!!target} onClose={onClose} title={target?.name || ""} wide>
      {target && (
        <div data-testid="sales-target-detail" className="space-y-4">
          <div className="flex items-center gap-6">
            <AdminGauge pct={target.progress_pct} />
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#4A5568]">{METRIC_LABEL[target.metric]} · {target.start_date} → {target.end_date}</div>
              <div className="font-display text-2xl font-bold text-[#0A1128] mt-1">{target.achieved_value.toLocaleString("en-IN")} / {target.target_value.toLocaleString("en-IN")}</div>
              <div className="flex gap-1.5 mt-2 flex-wrap">
                {target.states.map((s) => <span key={s} className="text-[10px] font-mono px-2 py-0.5 border border-[#E2E4DC] text-[#4A5568]"><MapPin className="w-2.5 h-2.5 inline mr-0.5" />{s}</span>)}
              </div>
              <div className="inline-flex items-center gap-1.5 mt-2 text-[11px] font-mono text-[#6D28D9] bg-[#F5F3FF] px-2 py-1" data-testid="sales-target-detail-incentive">
                <Gift className="w-3 h-3" /> ₹{(target.incentive_amount || 0).toLocaleString("en-IN")} incentive per qualifying SC on full achievement
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <ChartCard title="SC-wise Achievement" testid="sales-target-by-sc">
              <MiniBar data={target.by_sc.slice(0, 8).map((s) => ({ name: s.sc_name.slice(0, 12), value: s.achieved }))} xKey="name" dataKey="value" color="#1F4E2C" />
            </ChartCard>
            <ChartCard title="Region-wise Split" testid="sales-target-by-state">
              {target.by_state.length > 0 ? (
                <MiniPie data={target.by_state.map((s) => ({ name: s.state, value: s.achieved }))} colors={CHART_COLORS} />
              ) : <div className="text-[11px] font-mono text-[#94A3B8] text-center pt-16">No installs yet</div>}
            </ChartCard>
            <ChartCard title="Day-wise Trend" testid="sales-target-by-day">
              {target.by_day.length > 0 ? (
                <MiniBar data={target.by_day.map((d) => ({ name: d.date.slice(5), value: d.achieved }))} xKey="name" dataKey="value" color="#0EA5E9" />
              ) : <div className="text-[11px] font-mono text-[#94A3B8] text-center pt-16">No installs yet</div>}
            </ChartCard>
          </div>
        </div>
      )}
    </Modal>
  );
}

function TargetWizard({ open, onClose, onCreated }) {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(emptyForm);
  const [regions, setRegions] = useState([]);
  const [scs, setScs] = useState([]);
  const [scSearch, setScSearch] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setStep(1); setForm(emptyForm()); setScSearch("");
    api.get("/sales-targets/meta/regions").then((r) => {
      setRegions(r.data);
      setForm((f) => ({ ...f, states: r.data.map((x) => x.state) })); // all ticked by default
    });
  }, [open]);

  useEffect(() => {
    if (!open || form.states.length === 0) { setScs([]); return; }
    api.get(`/sales-targets/meta/scs?states=${encodeURIComponent(form.states.join(","))}`).then((r) => {
      setScs(r.data);
      setForm((f) => ({ ...f, sc_ids: r.data.map((x) => x.id) })); // all ticked by default
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, JSON.stringify(form.states)]);

  function toggleState(s) {
    setForm((f) => ({ ...f, states: f.states.includes(s) ? f.states.filter((x) => x !== s) : [...f.states, s] }));
  }
  function toggleAllStates() {
    setForm((f) => ({ ...f, states: f.states.length === regions.length ? [] : regions.map((r) => r.state) }));
  }
  function toggleSc(id) {
    setForm((f) => ({ ...f, sc_ids: f.sc_ids.includes(id) ? f.sc_ids.filter((x) => x !== id) : [...f.sc_ids, id] }));
  }
  function toggleAllScs() {
    setForm((f) => ({ ...f, sc_ids: f.sc_ids.length === scs.length ? [] : scs.map((s) => s.id) }));
  }
  function applyPreset(type) {
    let range;
    if (type === "this_month") range = monthRange(0);
    else if (type === "next_month") range = monthRange(1);
    else if (type === "this_quarter") range = quarterRange(0);
    else if (type === "next_quarter") range = quarterRange(1);
    if (range) setForm((f) => ({ ...f, period_type: type.includes("quarter") ? "quarter" : "month", start_date: range[0], end_date: range[1], preset: type }));
    else setForm((f) => ({ ...f, period_type: "custom", preset: "custom" }));
  }

  const filteredScs = useMemo(
    () => scs.filter((s) => !scSearch || (s.name || "").toLowerCase().includes(scSearch.toLowerCase()) || (s.city || "").toLowerCase().includes(scSearch.toLowerCase())),
    [scs, scSearch]
  );

  function canNext() {
    if (step === 1) return form.name.trim().length > 0;
    if (step === 2) return form.states.length > 0;
    if (step === 3) return form.sc_ids.length > 0;
    if (step === 4) return Number(form.target_value) > 0 && form.start_date && form.end_date && form.end_date >= form.start_date;
    if (step === 5) return Number(form.incentive_amount) > 0;
    return true;
  }

  async function submit() {
    setSaving(true);
    try {
      await api.post("/sales-targets", { ...form, target_value: Number(form.target_value), incentive_amount: Number(form.incentive_amount) });
      toast.success("Sales Target created — it's now live in the SC App");
      onCreated();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setSaving(false); }
  }

  return (
    <Modal open={open} onClose={onClose} title="New Sales Target" wide>
      <div data-testid="sales-target-wizard">
        {/* Step indicator */}
        <div className="flex items-center mb-6" data-testid="wizard-step-indicator">
          {STEP_TITLES.map((title, i) => {
            const n = i + 1;
            const active = n === step; const done = n < step;
            return (
              <div key={title} className="flex items-center flex-1 last:flex-none">
                <div className="flex flex-col items-center gap-1">
                  <div className={
                    "w-8 h-8 flex items-center justify-center font-mono text-xs font-bold " +
                    (done ? "bg-[#1F4E2C] text-white" : active ? "border-2 border-[#1F4E2C] text-[#1F4E2C]" : "border border-[#E2E4DC] text-[#94A3B8]")
                  } data-testid={`wizard-step-dot-${n}`}>
                    {done ? <Check className="w-4 h-4" /> : n}
                  </div>
                  <div className={"font-mono text-[9px] uppercase tracking-widest " + (active ? "text-[#1F4E2C] font-bold" : "text-[#94A3B8]")}>{title}</div>
                </div>
                {n < STEP_TITLES.length && <div className={"flex-1 h-[2px] mx-2 " + (done ? "bg-[#1F4E2C]" : "bg-[#E2E4DC]")} />}
              </div>
            );
          })}
        </div>

        {/* Step 1 — Name */}
        {step === 1 && (
          <div className="space-y-4" data-testid="wizard-step-1">
            <Field label="Target Name *">
              <TextInput autoFocus value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder='e.g. "Maharashtra Diwali Rooftop Drive"' data-testid="wizard-name-input" />
            </Field>
            <Field label="Notes (optional)">
              <TextInput value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Internal context for this target" data-testid="wizard-notes-input" />
            </Field>
          </div>
        )}

        {/* Step 2 — Region */}
        {step === 2 && (
          <div data-testid="wizard-step-2">
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm text-[#4A5568]">Tick the regions (Permissible Territories) this target covers.</div>
              <button onClick={toggleAllStates} className="font-mono text-[10px] uppercase tracking-widest text-[#1F4E2C] underline" data-testid="wizard-toggle-all-states">
                {form.states.length === regions.length ? "Deselect All" : "Select All"}
              </button>
            </div>
            <div className="border border-[#E2E4DC] max-h-72 overflow-y-auto divide-y divide-[#EEF2EA]">
              {regions.map((r) => (
                <label key={r.state} className="flex items-center justify-between px-3 py-2.5 cursor-pointer hover:bg-[#F4F6F1]" data-testid={`wizard-region-${r.state}`}>
                  <span className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.states.includes(r.state)} onChange={() => toggleState(r.state)} className="w-4 h-4" />{r.state}</span>
                  <span className="font-mono text-[10px] text-[#94A3B8]">{r.district_count} district(s) · {r.sc_count} SC(s)</span>
                </label>
              ))}
              {regions.length === 0 && <div className="px-3 py-6 text-center text-xs font-mono text-[#94A3B8]">No Permissible Territories configured yet — add some under Masters → Permissible Territories first.</div>}
            </div>
          </div>
        )}

        {/* Step 3 — SC coverage */}
        {step === 3 && (
          <div data-testid="wizard-step-3">
            <div className="flex items-center justify-between mb-2 gap-3 flex-wrap">
              <div className="text-sm text-[#4A5568]">{form.sc_ids.length} of {scs.length} Solar Consultant(s) covered. Deselect any you want to exclude.</div>
              <button onClick={toggleAllScs} className="font-mono text-[10px] uppercase tracking-widest text-[#1F4E2C] underline" data-testid="wizard-toggle-all-scs">
                {form.sc_ids.length === scs.length ? "Deselect All" : "Select All"}
              </button>
            </div>
            <div className="flex items-center gap-2 border border-[#E2E4DC] bg-white px-3 py-2 mb-2">
              <Search className="w-3.5 h-3.5 text-[#94A3B8]" />
              <input value={scSearch} onChange={(e) => setScSearch(e.target.value)} placeholder="Search by name or city…" className="bg-transparent outline-none text-sm font-mono w-full" data-testid="wizard-sc-search" />
            </div>
            <div className="border border-[#E2E4DC] max-h-72 overflow-y-auto divide-y divide-[#EEF2EA]">
              {filteredScs.map((s) => (
                <label key={s.id} className="flex items-center justify-between px-3 py-2.5 cursor-pointer hover:bg-[#F4F6F1]" data-testid={`wizard-sc-${s.id}`}>
                  <span className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.sc_ids.includes(s.id)} onChange={() => toggleSc(s.id)} className="w-4 h-4" />{s.name}</span>
                  <span className="font-mono text-[10px] text-[#94A3B8]">{s.city || s.state || "—"}</span>
                </label>
              ))}
              {scs.length === 0 && <div className="px-3 py-6 text-center text-xs font-mono text-[#94A3B8]">No Solar Consultants found for the selected region(s).</div>}
            </div>
          </div>
        )}

        {/* Step 4 — Target */}
        {step === 4 && (
          <div className="space-y-4" data-testid="wizard-step-4">
            <Field label="Metric *">
              <div className="flex gap-2">
                {["kw", "units"].map((m) => (
                  <button key={m} type="button" onClick={() => setForm({ ...form, metric: m })} data-testid={`wizard-metric-${m}`}
                    className={"flex-1 py-3 font-mono text-xs uppercase tracking-widest border-2 " + (form.metric === m ? "border-[#1F4E2C] bg-[#EEF2EA] text-[#1F4E2C]" : "border-[#E2E4DC] text-[#4A5568]")}>
                    {METRIC_LABEL[m]}
                  </button>
                ))}
              </div>
            </Field>
            <Field label={`Target Value (${form.metric === "kw" ? "kW" : "Units"}) *`}>
              <TextInput type="number" min={0} step="0.1" value={form.target_value} onChange={(e) => setForm({ ...form, target_value: e.target.value })} data-testid="wizard-target-value" />
            </Field>
            <Field label="Period *">
              <div className="flex gap-2 flex-wrap mb-2">
                {[["this_month", "This Month"], ["next_month", "Next Month"], ["this_quarter", "This Quarter"], ["next_quarter", "Next Quarter"], ["custom", "Custom"]].map(([k, l]) => (
                  <button key={k} type="button" onClick={() => applyPreset(k)} data-testid={`wizard-preset-${k}`}
                    className={"px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest border transition-colors " +
                      (form.preset === k ? "border-[#1F4E2C] bg-[#1F4E2C] text-white" : "border-[#E2E4DC] hover:border-[#1F4E2C] text-[#4A5568] hover:text-[#1F4E2C]")}>
                    {l}
                  </button>
                ))}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <TextInput type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value, period_type: "custom", preset: "custom" })} data-testid="wizard-start-date" />
                <TextInput type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value, period_type: "custom", preset: "custom" })} data-testid="wizard-end-date" />
              </div>
            </Field>
          </div>
        )}

        {/* Step 5 — Incentive */}
        {step === 5 && (
          <div className="space-y-4" data-testid="wizard-step-5">
            <Field label="Incentive Amount per SC (₹) *">
              <TextInput type="number" min={0} step="1" value={form.incentive_amount}
                onChange={(e) => setForm({ ...form, incentive_amount: e.target.value })}
                placeholder="e.g. 2000" data-testid="wizard-incentive-amount" />
            </Field>
            <div className="flex items-start gap-2.5 bg-[#F5F3FF] border border-[#E9D5FF] px-3 py-2.5 text-xs text-[#6D28D9]">
              <Gift className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>This exact amount is paid to <b>every covered SC who contributes</b> — but only once the full target ({form.target_value || 0} {form.metric === "kw" ? "kW" : "Units"}) is achieved by {form.end_date || "the end date"}. It then appears as a <b>Pending</b> entry under Referrer Payouts → Incentives, for you to mark Processing/Paid.</span>
            </div>
          </div>
        )}

        {/* Step 6 — Confirm */}
        {step === 6 && (
          <div className="space-y-3" data-testid="wizard-step-6">
            <div className="border border-[#E2E4DC] p-4 space-y-3">
              <div><div className="font-mono text-[10px] uppercase tracking-widest text-[#94A3B8]">Name</div><div className="font-display font-semibold text-lg">{form.name}</div></div>
              <div><div className="font-mono text-[10px] uppercase tracking-widest text-[#94A3B8]">Region</div><div className="flex gap-1.5 flex-wrap mt-1">{form.states.map((s) => <span key={s} className="text-[11px] font-mono px-2 py-0.5 bg-[#EEF2EA] text-[#1F4E2C]">{s}</span>)}</div></div>
              <div className="flex items-center gap-1.5"><Users className="w-3.5 h-3.5 text-[#94A3B8]" /><span className="font-mono text-sm">{form.sc_ids.length} Solar Consultant(s) covered</span></div>
              <div><div className="font-mono text-[10px] uppercase tracking-widest text-[#94A3B8]">Target</div><div className="font-display text-xl font-bold text-[#1F4E2C]">{form.target_value || 0} {form.metric === "kw" ? "kW" : "Units"}</div></div>
              <div><div className="font-mono text-[10px] uppercase tracking-widest text-[#94A3B8]">Period</div><div className="font-mono text-sm">{form.start_date} → {form.end_date}</div></div>
              <div><div className="font-mono text-[10px] uppercase tracking-widest text-[#94A3B8]">Incentive</div><div className="font-display text-xl font-bold text-[#6D28D9] flex items-center gap-1.5"><Gift className="w-4 h-4" /> ₹{Number(form.incentive_amount || 0).toLocaleString("en-IN")} / SC</div></div>
            </div>
            <p className="text-xs text-[#94A3B8]">Confirming will make this target live immediately — every covered SC will see it in their SC App.</p>
          </div>
        )}

        {/* Nav */}
        <div className="flex justify-between items-center pt-5 mt-5 border-t border-[#E2E4DC]">
          <GhostButton onClick={() => (step === 1 ? onClose() : setStep(step - 1))} testid="wizard-back-btn">
            <ChevronLeft className="w-3.5 h-3.5 inline mr-1" /> {step === 1 ? "Cancel" : "Back"}
          </GhostButton>
          {step < STEP_TITLES.length ? (
            <PrimaryButton onClick={() => canNext() && setStep(step + 1)} disabled={!canNext()} testid="wizard-next-btn">
              Next <ChevronRight className="w-3.5 h-3.5" />
            </PrimaryButton>
          ) : (
            <PrimaryButton onClick={submit} disabled={saving} testid="wizard-confirm-btn">
              {saving ? "Creating…" : <><Target className="w-3.5 h-3.5" /> Confirm &amp; Create</>}
            </PrimaryButton>
          )}
        </div>
      </div>
    </Modal>
  );
}
